// Phase 9 Plan 03 Task 2 — Cycle detector (D-02 iterative DFS + parent-pointer table).
//
// GRAPH MODEL:
//   Each constraint is a node. An edge X → Y exists when X's predicate/trigger reads
//   a widget state path that Y's effect WRITES. Direction: "X depends on Y".
//
// EDGE-INFERENCE RULES:
//   - dependency.if.ref          (consumer reads)  →  any constraint whose effect writes this StateRef
//   - visibility.when.ref        (consumer reads)  →  any constraint whose effect writes this StateRef
//   - propagation.trigger        (consumer reads)  →  any constraint whose effect writes this StateRef
//   - cardinality                NO edges in v1
//   - selectability              NO edges in v1 (Q-10 — leaf grammar carries no widgetInstanceId)
//
//   "Effect writes" comes from:
//     - propagation.trigger      (writer key = `propagation-effect::${stateRefKey(c.trigger)}`)
//     - dependency.require       (writer key = stateRefKey(c.require))
//
//   Propagation uses a DEDICATED `propagation-effect::` writer-key namespace so
//   that an isolated propagation's writer key is distinct from its consumer-read
//   key by construction. This closes D-DEF-09-03 (Plan 09-14): propagation's
//   effect logically writes a "mirror across batch sets" projection, not back to
//   the same StateRef the consumer reads.
//
// SELF-LOOP CASE:
//   A dependency where if.ref and require are the same StateRef yields edge
//   constraint-N → constraint-N. The detector emits a 1-node cycle with chain
//   ["constraint-N", "constraint-N"] (length 2).
//
//   Propagation does NOT self-loop — the writer key `propagation-effect::X` is
//   intentionally distinct from the consumer-read key `X` so a single
//   propagation never sees itself as a writer (D-DEF-09-03 closed by Plan 09-14).
//
// Q-02: constraint ids are document-position derived (`constraint-${index}`). Reorder
//   changes the ids — the renderer always looks up by document position; no schema id.
//
// Q-10 (limitation): v1 selectability leaf grammar carries no widgetInstanceId
//   reference. Selectability constraints contribute ZERO edges to the dependency graph
//   and cannot participate in cycle detection in v1. When the leaf grammar widens
//   (Phase 15+) to include `widget.{id}.state.{path}`, the buildDependencyEdges
//   `case "selectability"` branch must walk the predicate and emit edges.
//
// PARANOID-STRICT:
//   - Iterative DFS — explicit stack, never recursive.
//   - state: Map<id, 0|1|2> (unvisited / in-progress / done).
//   - parent: Map<id, id | null> for chain reconstruction.
//   - Bounded by V (V ≤ 100 per AST constraints).

import type { Constraint, StateRef } from "../schemas/constraint.js";
import type { CycleError } from "./types.js";

/**
 * D-02: detect cycles in the dependency graph implied by constraints[].
 *
 * Returns ALL cycles (deduplicated by sorted participants join). Empty array if acyclic.
 *
 * O(V + E) where V ≤ 100 (predicate AST max) and E ≤ V (each consumer has ≤ V writers).
 * Worst-case fully connected = O(V²) = 10K ops — well under the 16ms keystroke budget.
 */
export function detectCycles(constraints: ReadonlyArray<Constraint>): ReadonlyArray<CycleError> {
  if (constraints.length === 0) return [];

  // Build the directed edge list.
  const edges = buildDependencyEdges(constraints);
  const ids: ReadonlyArray<string> = constraints.map((_, i) => `constraint-${i}`);

  // DFS state per node.
  const state: Map<string, 0 | 1 | 2> = new Map(ids.map((id) => [id, 0 as const]));
  const parent: Map<string, string | null> = new Map(ids.map((id) => [id, null]));
  const cycles: CycleError[] = [];

  for (const startId of ids) {
    if (state.get(startId) !== 0) continue;

    // Iterative DFS with explicit stack of (nodeId, edgeIdx) frames.
    const stack: Array<{ id: string; edgeIdx: number }> = [{ id: startId, edgeIdx: 0 }];
    state.set(startId, 1);
    parent.set(startId, null);

    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      if (frame === undefined) break;
      const outgoing = edges.get(frame.id) ?? [];

      if (frame.edgeIdx >= outgoing.length) {
        // Done with this node — mark complete and pop.
        state.set(frame.id, 2);
        stack.pop();
        continue;
      }

      // noUncheckedIndexedAccess defensive narrowing.
      const next = outgoing[frame.edgeIdx];
      frame.edgeIdx++;
      if (next === undefined) continue;

      const nextState = state.get(next);

      if (nextState === 1) {
        // CYCLE: `next` is on the current DFS path. Walk parents from frame.id
        // back until we hit `next`; that's the cycle chain.
        const chain: string[] = [];
        let cursor: string | null = frame.id;
        // Walk back via parent pointers; stop when we reach `next`.
        // Bounded by V (parent chain length ≤ V).
        let safety = 0;
        while (cursor !== null && cursor !== next && safety < ids.length + 1) {
          chain.push(cursor);
          cursor = parent.get(cursor) ?? null;
          safety++;
        }
        if (cursor === next) {
          chain.push(next);
        }
        chain.reverse();
        chain.push(next); // Close the loop: A → B → C → A.

        cycles.push({
          chain,
          chainText: chain.join(" → "),
          participants: new Set(chain),
        });
        continue;
      }

      if (nextState === 0) {
        state.set(next, 1);
        parent.set(next, frame.id);
        stack.push({ id: next, edgeIdx: 0 });
      }
      // nextState === 2: already fully explored — skip.
    }
  }

  // Deduplicate cycles (the same cycle can be discovered from multiple entry points).
  // Canonicalize by sorting the participants set and joining.
  const seen = new Set<string>();
  const unique: CycleError[] = [];
  for (const cycle of cycles) {
    const key = [...cycle.participants].sort().join(",");
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(cycle);
    }
  }
  return unique;
}

// ---------------------------------------------------------------------------
// Internal helpers (not exported — barrel skips these).
// ---------------------------------------------------------------------------

/**
 * Build the directed edge list — consumer → writer.
 *
 * The edge direction matches "consumer depends on writer". In the resulting graph,
 * a cycle is a closed dependency loop (X depends on Y which depends on … which
 * depends on X). DFS finds these via the in-progress (state=1) re-visit rule.
 *
 * Conservative approximation: when uncertain, ADD the edge. Over-detection
 * surfaces a cycle the user can dismiss; under-detection lets a real cycle
 * crash render.
 */
function buildDependencyEdges(
  constraints: ReadonlyArray<Constraint>,
): ReadonlyMap<string, ReadonlyArray<string>> {
  const edges = new Map<string, string[]>();
  for (let i = 0; i < constraints.length; i++) {
    edges.set(`constraint-${i}`, []);
  }

  // Build writers index: stateRefKey → constraintId[].
  const writers = new Map<string, string[]>();
  const addWriter = (key: string, id: string): void => {
    const existing = writers.get(key);
    if (existing === undefined) {
      writers.set(key, [id]);
    } else {
      existing.push(id);
    }
  };

  for (let i = 0; i < constraints.length; i++) {
    const c = constraints[i];
    if (c === undefined) continue;
    const id = `constraint-${i}`;
    if (c.kind === "propagation") {
      // D-DEF-09-03: namespace the propagation EFFECT writer key so it is
      // distinct from the consumer-read key. Propagation's effect logically
      // mirrors the trigger value across batch sets — it does NOT write back
      // to the same StateRef the consumer reads. Plan 09-14.
      addWriter(`propagation-effect::${stateRefKey(c.trigger)}`, id);
    } else if (c.kind === "dependency") {
      addWriter(stateRefKey(c.require), id);
    }
    // cardinality + visibility + selectability are NOT writers in v1.
  }

  // Connect consumers to writers.
  for (let i = 0; i < constraints.length; i++) {
    const c = constraints[i];
    if (c === undefined) continue;
    const consumerId = `constraint-${i}`;
    const consumed: string[] = [];

    switch (c.kind) {
      case "dependency":
        consumed.push(stateRefKey(c.if.ref));
        break;
      case "visibility":
        consumed.push(stateRefKey(c.when.ref));
        break;
      case "propagation":
        consumed.push(stateRefKey(c.trigger));
        break;
      case "selectability":
        // Q-10: v1 selectability leaf grammar has no widgetInstanceId → no edges.
        // When the grammar widens (Phase 15+), walk c.predicate here and emit
        // an edge for each widget reference found in the leaves.
        break;
      case "cardinality":
        // Cardinality reads its scope but does not link to writers in v1.
        break;
      default: {
        // Compile-time exhaustiveness — adding a 6th constraint kind without
        // updating this dispatcher fails the typecheck.
        const _exhaustive: never = c;
        void _exhaustive;
        break;
      }
    }

    for (const key of consumed) {
      const consumerEdges = edges.get(consumerId);
      if (consumerEdges === undefined) continue;
      for (const writerId of writers.get(key) ?? []) {
        // SELF-LOOP CASE: if consumer reads a StateRef that consumer also writes
        // (dependency where if.ref === require), the writer === consumer and we
        // emit the self-edge. The DFS detector handles this via the in-progress
        // state=1 visit on revisit during the same DFS frame's outgoing-edge walk.
        consumerEdges.push(writerId);
      }
    }
  }

  return edges;
}

function stateRefKey(ref: StateRef): string {
  return `${ref.widgetInstanceId}::${ref.pathInState}`;
}
